import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import {
  baseTextSize,
  secondaryText,
  smallTextSize,
  whiteBG,
  borderPrimary,
  mediumText,
  primaryText,
  darkGreenColor,
  tertiaryText,
  regularText
} from '../../styling/stylingHelper';
import { Check } from 'lucide-react';
import { ColumnType, renderByType } from './TableValueComponent';
import { formatPlatformName } from '../../services/helperService';
import { Platform } from '../../types/Platforms';
import { SupplierLogo } from './SupplierLogo';
import { useAppSettingsStore } from '../../stores/appSettingsStore';

export type ValueDisplayType = ColumnType | 'default';

interface MultiSelectFilterProps {
  label: string;
  selectedValues: string[];
  onValuesChange: (values: string[]) => void;
  availableOptions: string[] | { id: string; name: string }[];
  valueType?: ValueDisplayType;
  maxHeight?: string;
  /** One option per row — for narrow popovers where 2 columns truncate labels. */
  singleColumn?: boolean;
}

export const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  label,
  selectedValues,
  onValuesChange,
  availableOptions,
  valueType = 'default',
  maxHeight = 'max-h-[100px]',
  singleColumn = false,
}) => {
  const darkMode = useAppSettingsStore((s) => s.darkMode);
  const { t } = useTranslation();
  // Handle toggle for string array or object array
  const handleToggle = (value: string) => {
    if (selectedValues.includes(value)) {
      onValuesChange(selectedValues.filter(v => v !== value));
    } else {
      onValuesChange([...selectedValues, value]);
    }
  };

  // Check if option is an object with id/name
  const isObjectArray = availableOptions.length > 0 && typeof availableOptions[0] === 'object';

  /** The values this list currently offers — all Select All ever writes. */
  const optionValues = isObjectArray
    ? (availableOptions as { id: string; name: string }[]).map(opt => opt.id)
    : (availableOptions as string[]);

  /**
   * Every offered option already picked. Select All hides only then — while the selection is
   * PARTIAL both buttons show, so "add the rest" stays one click instead of clear-then-select-all.
   */
  const allSelected = optionValues.length > 0 && optionValues.every(v => selectedValues.includes(v));

  const handleSelectAll = () => {
    onValuesChange(optionValues);
  };

  // Handle clear all
  const handleClearAll = () => {
    onValuesChange([]);
  };

  // Supplier options read as logo + full name (same visual language as the
  // platform table column) — a truncated slug like "altse..." is unreadable.
  const isPlatformType = valueType === 'platform';

  // Render the display value based on valueType
  const renderDisplayValue = (value: string, displayText?: string) => {
    if (isPlatformType) {
      return (
        <div className="flex items-center gap-2 min-w-0">
          <SupplierLogo
            platform={value as Platform}
            alt=""
            className="object-cover"
            // Suppliers without a logo file fall back to a missing image — hide it
            // and let the name carry the row instead of showing a broken icon.
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <span className={cn(primaryText, smallTextSize, mediumText, 'truncate')}>
            {displayText || formatPlatformName(value)}
          </span>
        </div>
      );
    }

    if (valueType === 'default') {
      return (
        // title: these labels can carry a disambiguating tail (an owner email) that truncates away.
        <span className={cn(primaryText, smallTextSize, mediumText, 'truncate block')} title={displayText || value}>
          {displayText || value}
        </span>
      );
    }

    // For category type, we pass the value to renderByType
    if (valueType === 'category') {
      // Wrap in a div to ensure truncation works with styled category badges
      return <div className="truncate">{renderByType(value, valueType, undefined, undefined, undefined, undefined, undefined, darkMode)}</div>;
    }

    // For subcategory type, we pass the value to renderByType
    if (valueType === 'subcategory') {
      // Wrap in a div to ensure truncation works with styled subcategory badges
      return <div className="truncate">{renderByType(value, 'subcategory' as any, undefined, undefined, undefined, undefined, undefined, darkMode)}</div>;
    }

    // For blueQuantity type, pass the display text to renderByType
    if (valueType === 'blueQuantity') {
      return <div className="truncate">{renderByType(displayText || value, valueType)}</div>;
    }

    // For other types, just display the text
    return (
      <span className={cn(primaryText, smallTextSize, mediumText, 'truncate block')}>
        {displayText || value}
      </span>
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <label className={cn(mediumText, baseTextSize, secondaryText)}>
          {label}
        </label>
        <div className="flex items-center gap-3">
          {!allSelected && (
            <button
              onClick={handleSelectAll}
              className={cn(smallTextSize, tertiaryText, regularText, 'hover:underline')}
            >
              {t('common.selectAll')}
            </button>
          )}
          {selectedValues.length > 0 && (
            <button
              onClick={handleClearAll}
              className={cn(smallTextSize, tertiaryText, regularText, 'hover:underline')}
            >
              {t('common.clearAll')}
            </button>
          )}
        </div>
      </div>
      <div className={cn('grid gap-1 overflow-y-auto', singleColumn || isPlatformType ? 'grid-cols-1' : 'grid-cols-2', maxHeight)}>
        {isObjectArray ? (
          (availableOptions as { id: string; name: string }[]).map((option) => {
            const isSelected = selectedValues.includes(option.id);
            return (
              <button
                key={option.id}
                onClick={() => handleToggle(option.id)}
                className={cn(
                  'px-2 py-1.5 transition-colors text-left',
                  'flex items-center gap-2 min-w-0',
                  whiteBG,
                  'hover:bg-gray-50 dark:hover:bg-white/5'
                )}
              >
                <div
                  className={cn(
                    'flex-shrink-0 w-4 h-4 rounded flex items-center justify-center border',
                    isSelected
                      ? 'border-transparent dark:border-transparent'
                      : borderPrimary
                  )}
                  style={{
                    backgroundColor: isSelected ? darkGreenColor : 'transparent'
                  }}
                >
                  {isSelected && <Check className="w-3 h-3 text-white dark:text-white" />}
                </div>
                <div className="min-w-0 flex-1 overflow-hidden">
                  {renderDisplayValue(option.id, option.name)}
                </div>
              </button>
            );
          })
        ) : (
          (availableOptions as string[]).map((option) => {
            const isSelected = selectedValues.includes(option);
            return (
              <button
                key={option}
                onClick={() => handleToggle(option)}
                className={cn(
                  'px-2 py-1.5 transition-colors text-left',
                  'flex items-center gap-2 min-w-0',
                  whiteBG,
                  'hover:bg-gray-50 dark:hover:bg-white/5'
                )}
              >
                <div
                  className={cn(
                    'flex-shrink-0 w-4 h-4 rounded flex items-center justify-center border',
                    isSelected
                      ? 'border-transparent dark:border-transparent'
                      : borderPrimary
                  )}
                  style={{
                    backgroundColor: isSelected ? darkGreenColor : 'transparent'
                  }}
                >
                  {isSelected && <Check className="w-3 h-3 text-white dark:text-white" />}
                </div>
                <div className="min-w-0 flex-1 overflow-hidden">
                  {renderDisplayValue(option)}
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};
